import { DateTime } from 'luxon';
import type {
    AppColumn,
    AppFilterClause,
    AppFilterDraft,
    AppFilterState,
    AppLookupOption,
    AppScalarValue,
} from './AppTable.types';

export const appFilterOperators = {
    text: ['contains', 'equals', 'startsWith', 'isEmpty'],
    number: ['equals', 'notEquals', 'greaterThan', 'atLeast', 'lessThan', 'atMost', 'isEmpty'],
    date: ['on', 'before', 'onOrBefore', 'after', 'onOrAfter', 'isEmpty'],
    dateTime: ['on', 'before', 'onOrBefore', 'after', 'onOrAfter', 'isEmpty'],
    boolean: ['equals'],
    lookup: ['equals', 'in', 'isEmpty'],
} as const;

const empty = (value: AppScalarValue): boolean => value === null || value === undefined || value === '';

function asDateTime(value: AppScalarValue): DateTime | undefined {
    const parsed =
        value instanceof Date
            ? DateTime.fromJSDate(value)
            : typeof value === 'string'
              ? DateTime.fromISO(value, {
                    setZone: true,
                })
              : undefined;

    return parsed?.isValid ? parsed : undefined;
}

function matchesFilter(value: AppScalarValue, filter: AppFilterClause): boolean {
    switch (filter.scalarType) {
        case 'text':
            if (filter.operator === 'isEmpty') {
                return empty(value);
            }
            if (filter.operator === 'contains' && filter.value === '') {
                return true;
            }
            if (typeof value !== 'string') {
                return false;
            }
            if (filter.operator === 'contains') {
                return value.toLocaleLowerCase().includes(filter.value.toLocaleLowerCase());
            }
            if (filter.operator === 'startsWith') {
                return value.toLocaleLowerCase().startsWith(filter.value.toLocaleLowerCase());
            }
            return value.toLocaleLowerCase() === filter.value.toLocaleLowerCase();
        case 'number':
            if (filter.operator === 'isEmpty') {
                return empty(value);
            }
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return false;
            }
            switch (filter.operator) {
                case 'equals':
                    return value === filter.value;
                case 'notEquals':
                    return value !== filter.value;
                case 'greaterThan':
                    return value > filter.value;
                case 'atLeast':
                    return value >= filter.value;
                case 'lessThan':
                    return value < filter.value;
                case 'atMost':
                    return value <= filter.value;
            }
            return false;
        case 'date':
        case 'dateTime': {
            if (filter.operator === 'isEmpty') {
                return empty(value);
            }

            const actual = asDateTime(value);

            if (!actual) {
                return false;
            }

            const expected = DateTime.fromJSDate(filter.value);

            if (!expected.isValid) {
                return false;
            }

            const actualCalendarDate = actual.toISODate();
            const expectedCalendarDate = expected.toISODate();

            if (filter.operator === 'on') {
                return filter.scalarType === 'date'
                    ? actualCalendarDate === expectedCalendarDate
                    : actual.toMillis() === expected.toMillis();
            }

            if (!actualCalendarDate || !expectedCalendarDate) {
                return false;
            }

            const comparison = filter.scalarType === 'date'
                ? actualCalendarDate.localeCompare(expectedCalendarDate)
                : actual.toMillis() - expected.toMillis();

            switch (filter.operator) {
                case 'before':
                    return comparison < 0;
                case 'onOrBefore':
                    return comparison <= 0;
                case 'after':
                    return comparison > 0;
                case 'onOrAfter':
                    return comparison >= 0;
            }
            return false;
        }
        case 'boolean':
            return filter.operator === 'equals' && value === filter.value;
        case 'lookup':
            if (filter.operator === 'in') {
                return filter.value.some(candidate => Object.is(value, candidate));
            }

            return filter.operator === 'isEmpty' ? empty(value) : Object.is(value, filter.value);
    }
}

export function applyAppFilters<TRow>(
    rows: readonly TRow[],
    columns: readonly AppColumn<TRow>[],
    filters: readonly AppFilterState[],
    logic: 'and' | 'or',
): TRow[] {
    if (filters.length === 0) {
        return [...rows];
    }

    const columnsById = new Map(columns.map((column) => [column.id, column]));
    const activeFilters = filters.filter((filter): filter is AppFilterClause => {
        if (filter.scalarType === 'draft') {
            return false;
        }
        const column = columnsById.get(filter.columnId);

        return (
            column !== undefined &&
            column.kind !== 'action' &&
            column.filterable !== false &&
            column.scalarType === filter.scalarType
        );
    });

    if (activeFilters.length === 0) {
        return [...rows];
    }

    return rows.filter((row) => {
        const results = activeFilters.map((filter) => {
            const column = columnsById.get(filter.columnId);

            const matches = column === undefined ? true : matchesFilter(column.accessor(row), filter);

            return filter.negated ? !matches : matches;
        });

        return logic === 'and' ? results.every(Boolean) : results.some(Boolean);
    });
}

export function getAppLookupOptions<TRow>(
    column: AppColumn<TRow>,
    rows: readonly TRow[] = [],
): AppLookupOption[] {
    const options: AppLookupOption[] = [...(column.lookup?.options ?? [])];
    const seenValues = new Set(options.map((option) => option.value));

    for (const row of rows) {
        const value = column.accessor(row);

        if (value === null || value === undefined || value === '') {
            continue;
        }
        if (seenValues.has(value)) {
            continue;
        }

        seenValues.add(value);
        options.push({
            value,
            label: column.lookup?.labelFor?.(value, row) ?? String(value),
        });
    }

    return options;
}

export function createEmptyAppFilter<TRow>(column?: AppColumn<TRow>): AppFilterDraft {
    return {
        columnId: column?.id ?? '',
        scalarType: 'draft',
        ...(column ? { targetScalarType: column.scalarType } : {}),
        operator: '',
    };
}

export function isAppFilterable<TRow>(column: AppColumn<TRow>): boolean {
    return column.kind !== 'action' && column.filterable !== false;
}

export function isAppScalarEmpty(value: AppScalarValue): boolean {
    return empty(value);
}
